import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Building2, IndianRupee, Plus, Minus, ClipboardList, Landmark } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { KIND_LABEL, TYPE_LABEL, statementExcel, statementPdf } from "@/lib/payoutExport";
import { Cards, StatementTable, fmtD, inr } from "./PayoutBits";
import { DownloadMenu } from "./DownloadMenu";
import { AgenciesForm, EntryForm, Modal, RateForm } from "./PayoutForms";
import { OpeningForm } from "./OpeningForms";
import { QuickEntry } from "./QuickEntry";

const btn = "text-xs flex items-center gap-1 px-3 py-1.5 rounded border transition-colors";

export function RatesList({ person, onDelete }) {
  if (person.kind === "booker") return null;
  return (
    <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="payout-rates-list">
      {(person.rates || []).map((r) => (
        <span key={r.from_date} className="flex items-center gap-1 px-2 py-0.5 rounded bg-[#F0F0EA] font-mono">
          {fmtD(r.from_date)} onward: {person.kind === "salesman" ? inr(r.rate) : `I ${inr(r.indane)} · B ${inr(r.bharat)} · H ${inr(r.hp)}`}
          {onDelete && <button onClick={() => onDelete(r.from_date)} className="text-[#C8553D]" title="Delete rate" data-testid={`payout-rate-del-${r.from_date}`}>×</button>}
        </span>
      ))}
      {!person.rates?.length && <span className="text-[#C8553D]">Rate not set — earnings will be ₹0</span>}
    </div>
  );
}

export function PersonPanel({ personId, range, onClose, onChanged }) {
  const [st, setSt] = useState(null);
  const [form, setForm] = useState(null);
  const load = useCallback(() => {
    let alive = true;
    api.get(`/payouts/people/${personId}/statement`, { params: { from_date: range.from, to_date: range.to } })
      .then(({ data }) => alive && setSt(data)).catch((e) => toast.error(formatApiError(e)));
    return () => { alive = false; };
  }, [personId, range.from, range.to]);
  useEffect(() => load(), [load]);
  const refresh = () => { setForm(null); load(); onChanged(); };
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); refresh(); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!st) return <Modal title="Loading…" onClose={onClose}><Loader2 className="w-5 h-5 animate-spin" /></Modal>;
  const p = st.person;
  const delRate = (d) => window.confirm(`Delete the rate effective from ${fmtD(d)}?`) && act(() => api.delete(`/payouts/people/${p.id}/rates/${d}`), "Rate deleted");
  const ob = st.opening_balance;
  const delOpening = () => window.confirm("Remove the opening balance? All earlier entries will be counted again.") && act(() => api.delete(`/payouts/people/${p.id}/opening`), "Opening balance removed");
  const delRow = (r) => window.confirm(`Delete this ${inr(r.amount)} ${TYPE_LABEL[r.type].toLowerCase()} entry of ${fmtD(r.date)}? Only this entry will be removed.`) && act(() => api.delete(`/payouts/entries/${r.id}`), "Entry deleted");
  return (
    <Modal title={`${KIND_LABEL[p.kind]} · ${p.name}`} onClose={onClose} size="max-w-5xl" testid="payout-person-panel">
      {!p.username && (
        <div className="text-xs text-[#6B6B61]" data-testid="payout-panel-login">Not linked to a user — create a user with the same name in Admin → Users.</div>
      )}
      <RatesList person={p} onDelete={delRate} />
      <div className="flex flex-wrap gap-2">
        {p.kind === "booker" && <button onClick={() => setForm({ t: "entry", kind: "work" })} className={`${btn} border-[#385C4B] text-[#385C4B]`} data-testid="payout-add-work"><ClipboardList className="w-3.5 h-3.5" />+ Work Entry</button>}
        <button onClick={() => setForm({ t: "entry", kind: "payment" })} className={`${btn} bg-[#385C4B] border-[#385C4B] text-white`} data-testid="payout-add-payment"><Plus className="w-3.5 h-3.5" />Payment</button>
        <button onClick={() => setForm({ t: "entry", kind: "minus" })} className={`${btn} border-[#C8553D] text-[#C8553D]`} data-testid="payout-add-minus"><Minus className="w-3.5 h-3.5" />Deduction</button>
        <button onClick={() => setForm({ t: "opening" })} className={`${btn} border-[#B7791F] text-[#B7791F]`} data-testid="payout-set-opening">
          <Landmark className="w-3.5 h-3.5" />{ob ? `Opening ${inr(ob.amount)} · from ${fmtD(ob.date)}` : "Opening Balance"}
        </button>
        {p.kind !== "booker" && <button onClick={() => setForm({ t: "rate" })} className={`${btn} border-[#DCDCD3]`} data-testid="payout-change-rate"><IndianRupee className="w-3.5 h-3.5" />Change Rate</button>}
        {p.kind === "salesman" && <button onClick={() => setForm({ t: "agencies" })} className={`${btn} border-[#DCDCD3]`} data-testid="payout-edit-agencies"><Building2 className="w-3.5 h-3.5" />Agencies</button>}
      </div>
      <QuickEntry key={p.id} person={p} ob={ob} onSaved={refresh} />
      <Cards s={st} showOpening />
      <div className="flex items-center justify-between">
        <div className="text-xs text-[#6B6B61]">Qty: <b data-testid="payout-panel-qty">{st.qty}</b></div>
        <DownloadMenu prefix="payout-statement" onExcel={statementExcel} onPdf={statementPdf}
          fetcher={async (r) => (await api.get(`/payouts/people/${p.id}/statement`, { params: { from_date: r.from, to_date: r.to } })).data} />
      </div>
      <StatementTable st={st} onEdit={(r) => setForm({ t: "entry", kind: r.kind, entry: r })} onDelete={delRow} onEditOpening={() => setForm({ t: "opening" })} onDeleteOpening={delOpening} />
      {form?.t === "entry" && <EntryForm person={p} kind={form.kind} entry={form.entry} onClose={() => setForm(null)} onSaved={refresh} />}
      {form?.t === "opening" && <OpeningForm person={p} ob={ob} onClose={() => setForm(null)} onSaved={refresh} onDelete={delOpening} />}
      {form?.t === "rate" && <RateForm person={p} onClose={() => setForm(null)} onSaved={refresh} />}
      {form?.t === "agencies" && <AgenciesForm person={p} onClose={() => setForm(null)} onSaved={refresh} />}
    </Modal>
  );
}
